import React, {useMemo} from 'react';

import {useGate, useStore} from 'effector-react';
import i18next, {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';
import 'core/yupCustomValidations';

import {toast} from 'react-toastify';

import {validateCode} from 'api';
import {ReactComponent as CrossIcon} from 'assets/svg/cross.svg';
import {ReactComponent as UploadIcon} from 'assets/svg/download.svg';
import {ReactComponent as ScanIcon} from 'assets/svg/scan.svg';
import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {Icon} from 'components/ui-kit/Icon';
import {StatusWrapper} from 'components/ui-kit/PageComponents';
import {QRScannerModal} from 'components/ui-kit/ScanCode';
import {Select} from 'components/ui-kit/Select';
import {mapTableColumns, Table} from 'components/ui-kit/Table2.0';
import {PRODUCT_STATUS_COLORS} from 'core/constants/statuses';
import {DocumentTypeEntity, ProductsPackageType, ProductsStatus} from 'core/dictionaries';
import {codesChanged, file$, fileChanged} from 'models/documents/create';
import {
    aggregationForm$,
    createAggregationDocument,
    sendAggregationFx,
    stepChanged,
} from 'models/documents/create/aggregation';
import {data$, dataChanged, Gate} from 'models/documents/create/aggregation/tables/childrenCodes';
import {GridFieldConfig} from 'types';
import {CodeRequestType, CodeResponseType, ValidatedProductsGridAccessors} from 'types/validation';
import {handleNil} from 'utils/handleNil';

import {PackageTypesOptions} from 'utils/packageTypes-mappers';
import {mapArrayToI18nOptions} from 'utils/select';

import {hasOnlyLatinLetters} from 'utils/string';

import {
    ActionsContainer,
    ControlContainer,
    CountContainer,
    HeadContainer,
    NoCodesContent,
    StepNavigationContainer,
    Wrapper,
} from '../common/styled';
import {UploadFileModal} from '../common/UploadFileModal';

const getConfig = (t: TFunction): GridFieldConfig[] => [
    {
        id: ValidatedProductsGridAccessors.name,
        header: t('pages.products.grid.productName'),
        maxWidth: 220,
    },
    {
        id: ValidatedProductsGridAccessors.code,
        header: t('pages.products.grid.code'),
    },
    {
        id: ValidatedProductsGridAccessors.packageType,
        header: t('pages.products.grid.package'),
        filter: (
            <Select
                isClearable
                options={mapArrayToI18nOptions(
                    PackageTypesOptions,
                    'dictionaries.products.packageType',
                )}
            />
        ),
        width: 170,
    },
    {
        id: ValidatedProductsGridAccessors.status,
        header: t('pages.products.grid.status'),
        width: 200,
    },
    {
        id: 'action',
        header: t('pages.products.grid.action'),
        width: 80,
    },
];

const convertData = (
    data: CodeResponseType['info'][] | undefined,
    t: TFunction,
): Partial<CodeResponseType['info'] & {action: React.ReactNode}>[] => {
    if (!data) return [];

    return data.map((elem) => ({
        [ValidatedProductsGridAccessors.name]: handleNil(elem.productName),
        [ValidatedProductsGridAccessors.code]: handleNil(elem.identityCode),
        [ValidatedProductsGridAccessors.status]: handleNil(elem.status, (value: ProductsStatus) => (
            <StatusWrapper color={PRODUCT_STATUS_COLORS[value ?? 'DEFAULT']}>
                {t(`dictionaries.products.status.${value}`)}
            </StatusWrapper>
        )),
        [ValidatedProductsGridAccessors.packageType]: handleNil(
            elem.packagingType,
            (value: ProductsPackageType) => t(`dictionaries.products.packageType.${value}`),
        ),
        action: (
            <ActionsContainer>
                <Icon
                    source={CrossIcon}
                    width={16}
                    height={16}
                    fillColor="#FF4B55"
                    onClick={() => dataChanged(elem)}
                />
            </ActionsContainer>
        ),
    }));
};

export const AggregationDocumentChildrenCodes: React.FC = () => {
    const {t} = useTranslation();

    useGate(Gate);
    const data = useStore(data$);
    const form = useStore(aggregationForm$);
    const uploadPending = useStore(sendAggregationFx.pending);

    const columns = useMemo(() => mapTableColumns(getConfig(t)), [t]);
    const mappedData = React.useMemo(() => convertData(data, t), [data, t]);

    const [scanShow, setScanShow] = React.useState(false);
    const [uploadShow, setUploadShow] = React.useState(false);
    const [pending, setPending] = React.useState(false);

    const commonValidation = (codeData: CodeResponseType, errors: string[] = []) => {
        if (!codeData) {
            errors.push('Invalid code');
        }

        errors.push(...codeData.errors.map((e) => e.message));
        if (data.find((d) => d.identityCode === codeData.info.identityCode))
            errors.push(i18next.t('components.scan.errors.double'));

        return errors;
    };

    const onEnterCode = async (code: string) => {
        try {
            setPending(true);
            if (!hasOnlyLatinLetters(code)) {
                toast.warning(t('components.scan.layoutWarn'));
                return;
            }

            const payload: CodeRequestType = {
                markingCode: code,
                aggregatorMarkingCode: form.aggregationNumber || undefined,
                storageLocationId: String(form.storageLocationId) || undefined,
                type: DocumentTypeEntity.AGGREGATE,
            };
            const response = (await validateCode(payload)).data;
            const status = commonValidation(response);
            if (status?.length) {
                toast.error(
                    <div>
                        <strong>Errors</strong>
                        <ul style={{margin: '6px 0'}}>
                            {status?.map((e) => (
                                <li key={e}>{e}</li>
                            ))}
                        </ul>
                    </div>,
                );
                return;
            }
            dataChanged(response.info);
        } catch (e) {
            console.error(e);
            toast.error(e.message ?? 'Invalid code');
        } finally {
            setPending(false);
        }
    };

    const file = useStore(file$);
    const resetUpload = () => {
        setUploadShow(false);
        codesChanged([]);
        fileChanged(null);
    };

    const EmptyMessage = () => (
        <NoCodesContent>
            <h3>{t('components.table.Scan.title')}</h3>
            <span>{t('components.table.Scan.description')}</span>
        </NoCodesContent>
    );

    return (
        <>
            <Wrapper>
                <HeadContainer>
                    <ControlContainer>
                        <Button onClick={() => setScanShow(true)}>
                            {t('components.scan.button')}
                            <Icon source={ScanIcon} width={20} height={20} />
                        </Button>
                        <Button variant={ButtonVariant.Outline} onClick={() => setUploadShow(true)}>
                            {t('components.upload.button')}
                            <Icon source={UploadIcon} width={16} height={16} />
                        </Button>
                    </ControlContainer>
                    <StepNavigationContainer>
                        <Button
                            style={{width: 'auto'}}
                            variant={ButtonVariant.Secondary}
                            onClick={() => stepChanged(1)}
                        >
                            {t('components.form.prevStep')}
                        </Button>
                        <Button
                            style={{width: 'auto'}}
                            variant={ButtonVariant.Normal}
                            onClick={() => createAggregationDocument()}
                            disabled={!data.length}
                        >
                            {t('pages.documents.form.aggregationSubmit')}
                            {!!data.length && <CountContainer>{data.length}</CountContainer>}
                        </Button>
                    </StepNavigationContainer>
                </HeadContainer>
                <Table
                    emptyStateComponent={EmptyMessage()}
                    columns={columns}
                    data={mappedData}
                    hideSetting
                />
            </Wrapper>
            <QRScannerModal
                open={scanShow}
                pending={pending}
                onClose={() => setScanShow(false)}
                onScan={onEnterCode}
                onManual={onEnterCode}
            />
            <UploadFileModal
                open={uploadShow}
                onClose={resetUpload}
                pending={uploadPending}
                onFileChange={fileChanged}
                initialFile={file}
                submit={() => (
                    <Button
                        onClick={() => createAggregationDocument()}
                        disabled={!file || uploadPending}
                    >
                        {t('pages.documents.form.aggregationSubmit')}
                    </Button>
                )}
            />
            {/*<XlsxFileModal*/}
            {/*    open={uploadShow}*/}
            {/*    onClose={resetUpload}*/}
            {/*    onFileChange={handleFileChanged}*/}
            {/*    submit={(valid) => (*/}
            {/*        <Button onClick={() => createAggregationDocument()} disabled={!valid}>*/}
            {/*            {t('pages.documents.form.aggregationSubmit')}*/}
            {/*        </Button>*/}
            {/*    )}*/}
            {/*    initialFile={file}*/}
            {/*/>*/}
        </>
    );
};
